import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import RegistrationDialog from "../components/RegistrationDialog";
import { Button } from "../components/ui/button";
import { MapPin, Calendar, Clock, Users, FileText, Bus, Utensils, Backpack, Loader2, ArrowLeft, Target } from "lucide-react";
import api from "../lib/api";

function fmtFull(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }); } catch { return ""; }
}

export default function ExperienceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regOpen, setRegOpen] = useState(false);

  useEffect(() => {
    api.get(`/experiences/${id}`).then((r) => setExp(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <PublicLayout><div className="flex justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-brandblue" /></div></PublicLayout>;
  if (!exp) return <PublicLayout><div className="py-40 text-center text-slateBrand">Experience not found.</div></PublicLayout>;

  const full = exp.spots_remaining <= 0;

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative h-[52vh] min-h-[380px] w-full overflow-hidden">
        <img src={exp.images?.[0]} alt={exp.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-10 lg:px-8">
          <button onClick={() => navigate("/experiences")} className="absolute left-5 top-6 flex items-center gap-1 text-sm text-white/80 hover:text-white lg:left-8" data-testid="exp-back-btn">
            <ArrowLeft className="h-4 w-4" />All Experiences
          </button>
          <span className="eyebrow text-exploration">{exp.category}</span>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-extrabold text-white lg:text-5xl">{exp.title}</h1>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/80">
            <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" />{fmtFull(exp.date)}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" />{exp.time}</span>
            <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{exp.location}</span>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-5 py-14 lg:grid-cols-3 lg:px-8">
        <div className="space-y-10 lg:col-span-2">
          <div>
            <h2 className="font-display text-2xl font-bold text-navy">About this experience</h2>
            <p className="mt-4 leading-relaxed text-slateBrand">{exp.description}</p>
          </div>

          {exp.objectives?.length > 0 && (
            <div>
              <h3 className="font-display text-xl font-bold text-navy">Educational objectives</h3>
              <ul className="mt-4 space-y-2">
                {exp.objectives.map((o, i) => (
                  <li key={i} className="flex items-start gap-3 text-slateBrand"><Target className="mt-1 h-4 w-4 shrink-0 text-brandblue" />{o}</li>
                ))}
              </ul>
            </div>
          )}

          {exp.itinerary?.length > 0 && (
            <div>
              <h3 className="font-display text-xl font-bold text-navy">Schedule</h3>
              <div className="mt-4 space-y-3">
                {exp.itinerary.map((it, i) => (
                  <div key={i} className="flex gap-4 rounded-xl border border-slate-200 bg-white p-4">
                    <span className="font-display font-bold text-brandblue">{it.time}</span>
                    <span className="text-slateBrand">{it.activity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-5 sm:grid-cols-2">
            {[
              { icon: Backpack, t: "What to bring", d: exp.what_to_bring },
              { icon: Utensils, t: "Lunch", d: exp.lunch_info },
              { icon: Users, t: "Ages & Grades", d: `${exp.age_min}–${exp.age_max} yrs • ${exp.grade_range}` },
              { icon: Bus, t: "Transportation", d: exp.transport_enabled ? `H.E.A. transport available ($${exp.transport_price})` : "Parent transportation" },
            ].filter((x) => x.d).map((x) => (
              <div key={x.t} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-2 font-display font-semibold text-navy"><x.icon className="h-4 w-4 text-brandblue" />{x.t}</div>
                <p className="mt-2 text-sm text-slateBrand">{x.d}</p>
              </div>
            ))}
          </div>

          {exp.forms_required?.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 font-display font-semibold text-navy"><FileText className="h-4 w-4 text-brandblue" />Required forms</div>
              <ul className="mt-3 space-y-1 text-sm text-slateBrand">
                {exp.forms_required.map((f) => <li key={f}>• {f}</li>)}
              </ul>
            </div>
          )}

          {exp.cancellation_policy && (
            <p className="text-sm text-slateBrand"><span className="font-semibold text-navy">Cancellation & refund policy:</span> {exp.cancellation_policy}</p>
          )}
        </div>

        {/* STICKY REGISTER CARD */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-slateBrand">Member</span>
              <span className="font-display text-2xl font-bold text-navy">{exp.credit_eligible ? "Included*" : (exp.member_price === 0 ? "Included" : `$${exp.member_price}`)}</span>
            </div>
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-slateBrand">Non-member</span>
              <span className="font-display text-2xl font-bold text-navy">${exp.non_member_price}</span>
            </div>
            {exp.credit_eligible && <p className="mt-2 text-xs text-slateBrand">*Included with membership credits, subject to availability.</p>}

            {exp.coming_soon ? (
              <>
                <Button disabled data-testid="exp-comingsoon-btn" className="mt-6 h-12 w-full bg-slate-300 font-display text-base font-semibold text-navy">Coming Soon</Button>
                <p className="mt-3 text-center text-xs text-slateBrand">Registration opens once H.E.A. publishes this experience.</p>
              </>
            ) : (
            <>
            <div className="mt-5 space-y-2 border-t border-slate-100 pt-5 text-sm">
              <div className="flex justify-between"><span className="text-slateBrand">Capacity remaining</span><span className="font-semibold text-navy">{exp.spots_remaining} / {exp.capacity}</span></div>
              {exp.registration_deadline && <div className="flex justify-between"><span className="text-slateBrand">Deadline</span><span className="font-semibold text-navy">{fmtFull(exp.registration_deadline)}</span></div>}
              {exp.scholarship_eligible && <div className="rounded-lg bg-gold/10 px-3 py-2 text-xs text-[#8A5F00]">May be scholarship eligible — confirm with your provider.</div>}
            </div>

            <Button data-testid="exp-register-btn" onClick={() => setRegOpen(true)}
              className="mt-6 h-12 w-full bg-brandblue font-display text-base font-semibold hover:bg-blue-700">
              {full && exp.waitlist_enabled ? "Join Waitlist" : "Register Now"}
            </Button>
            {exp.parent_attendance_required && <p className="mt-3 text-center text-xs text-alertBrand">Parent attendance required for this experience.</p>}
            </>
            )}
          </div>
        </div>
      </section>

      <RegistrationDialog open={regOpen} onOpenChange={setRegOpen} exp={exp} />
    </PublicLayout>
  );
}
